import React, { useEffect, useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Stack,
  Divider,
  TablePagination,
  InputAdornment,
  useMediaQuery,
  useTheme , 
  FormControlLabel
   , Checkbox
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Bed,
  LocalHospital,
  MonetizationOn,
  CheckCircle,
  Cancel,
  PersonAdd,
  People,
  PersonOutline,
  Hotel,
  Search
} from '@mui/icons-material';

import { addWardWithBeds, loadInsurancesddb,updateWarddb } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';
import { ErrorMessage, successMessage } from '../Message';
import { loadBedsdb, loadWardsdb, deleteWard ,deleteBed } from '../BaseURL';


// Row-fitting constants — module scope so the measuring callback below can
// keep an empty dependency array. WARD_ROW_H is taller than BED_ROW_H because
// ward rows carry the capacity progress bar under the name.
const WARD_ROW_H    = 59;
const BED_ROW_H     = 53;
const PAGINATION_H  = 52;
const BOTTOM_GUTTER = 16;
const MIN_ROWS      = 5;

export default function WardsBeds() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  const [applyAll, setApplyAll] = useState(false);
  const [globalDailyRate, setGlobalDailyRate] = useState("");
  const [globalDailyTariff, setGlobalDailyTariff] = useState("");

  // Search and Pagination States
  const [wardSearchQuery, setWardSearchQuery] = useState('');
  const [wardPage, setWardPage] = useState(0);
  const [wardRowsPerPage, setWardRowsPerPage] = useState(5);
  
  const [bedSearchQuery, setBedSearchQuery] = useState('');
  const [bedPage, setBedPage] = useState(0);
  const [bedRowsPerPage, setBedRowsPerPage] = useState(5);

 const [insurances, setInsurances] = useState([]);
 const [insuranceRates, setInsuranceRates] = useState([]);

  const [errors, setErrors] = useState({
    name: false,
    capacity: false,
    dailyRate: false
  });

  const [wards, setWards] = useState([]);
  const [beds, setBeds] = useState([]);


const loadInsurances = async () => {
  try {
    const res = await axios.get(loadInsurancesddb);

    if (res.data.success) {
      const insurancesList = res.data.insurances;
      setInsurances(insurancesList);

      // Initialize rates only if not editing
      if (!editingItem) {
        setInsuranceRates(
          insurancesList.map(i => ({
            insuranceId: i.id,
            name: i.name,
            dailyTariff: 0,
            dailyRate: 0
          }))
        );
      } else {
        // If editing, prepare the structure but don't overwrite
        const baseRates = insurancesList.map(i => ({
          insuranceId: i.id,
          name: i.name,
          dailyTariff: 0,
          dailyRate: 0
        }));
        
        setInsuranceRates(baseRates);
      }
      
      return insurancesList;
    }
  } catch (error) {
    console.error("Error loading insurances:", error);
    ErrorMessage("Error", "Failed to load insurance companies");
    return [];
  }
};


  const loadWards = async () => {
    const res = await axios.get(loadWardsdb);
    setWards(res.data.wards);
  };

  const loadBeds = async () => {
    const res = await axios.get(loadBedsdb);
    setBeds(res.data.beds);
  };

  const [formData, setFormData] = useState({
    name: '',
    type: '',
    capacity: '',
    dailyRate: '',
    bedNumber: '',
    wardId: '',
    status: 'Available'
  });

  const bedStatuses = ['Available', 'Occupied', 'Maintenance', 'Reserved'];

  useEffect(() => {
    loadWards();
    loadBeds();
  }, []);


useEffect(() => {
  if (openDialog && dialogType === "ward") {
    const initializeInsuranceRates = async () => {
      // Load all insurances
      const res = await axios.get(loadInsurancesddb);
      
      if (res.data.success) {
        const allInsurances = res.data.insurances;
        setInsurances(allInsurances);
        
        // Map insurance rates
        const mappedRates = allInsurances.map(insurance => {
          // If editing, find the matching rate from the ward
          if (editingItem && editingItem.insuranceRates) {
            const existingRate = editingItem.insuranceRates.find(
              rate => rate.insuranceId === insurance.id
            );
            
            if (existingRate) {
              return {
                insuranceId: insurance.id,
                name: insurance.name,
                dailyTariff: existingRate.dailyTariff,
                dailyRate: existingRate.dailyRate
              };
            }
          }
          
          // Default values for new ward or missing rates
          return {
            insuranceId: insurance.id,
            name: insurance.name,
            dailyTariff: 0,
            dailyRate: 0
          };
        });
        
        setInsuranceRates(mappedRates);
      }
    };
    
    initializeInsuranceRates();
  }
}, [openDialog, dialogType, editingItem]);


useEffect(() => {
  if (!applyAll) return;

  setInsuranceRates(prev =>
    prev.map(row => ({
      ...row,
      dailyRate: globalDailyRate,
      dailyTariff: globalDailyTariff
    }))
  );
}, [applyAll, globalDailyRate, globalDailyTariff]);


  // Ward filtering and pagination

  const filteredWards = useMemo(() => {
    return wards.filter((ward) => {
      const query = wardSearchQuery.toLowerCase();
      return (
        (ward.name || '').toLowerCase().includes(query) ||
        (ward.status || '').toLowerCase().includes(query) ||
        (ward.id?.toString() || '').includes(query)
      );
    });
  }, [wards, wardSearchQuery]);

  const paginatedWards = useMemo(() => {
    return filteredWards.slice(
      wardPage * wardRowsPerPage,
      wardPage * wardRowsPerPage + wardRowsPerPage
    );
  }, [filteredWards, wardPage, wardRowsPerPage]);

  // Bed filtering and pagination
  const filteredBeds = useMemo(() => {
    return beds.filter((bed) => {
      const query = bedSearchQuery.toLowerCase();
      return (
        (bed.bedNumber || '').toLowerCase().includes(query) 
      );
    });
  }, [beds, bedSearchQuery]);

  const paginatedBeds = useMemo(() => {
    return filteredBeds.slice(
      bedPage * bedRowsPerPage,
      bedPage * bedRowsPerPage + bedRowsPerPage
    );
  }, [filteredBeds, bedPage, bedRowsPerPage]);

  // ── Fit the table to the viewport ─────────────────────────────────────────
  // Both tables were pinned at 5 rows per page whatever the screen size, so on
  // a normal desktop the card stopped a third of the way down and left a large
  // dead area below it — while the remaining rows sat behind the pager.
  //
  // Rather than guess a bigger fixed number (which would overflow on a laptop
  // and still under-fill on a large monitor), measure the space actually left
  // under the table head and fit as many whole rows as it holds. The table
  // reaches the bottom of the viewport with no inner scrollbar, and adding
  // more wards or beds simply extends it downward until the space runs out.
  const wardTableRef = useRef(null);
  const bedTableRef  = useRef(null);

  // Once the user picks a page size by hand, stop overriding their choice.
  const [wardAutoFit, setWardAutoFit] = useState(true);
  const [bedAutoFit,  setBedAutoFit]  = useState(true);

  const measureRows = useCallback((el, rowHeight) => {
    if (!el) return null;
    // getBoundingClientRect is viewport-relative, so this stays correct
    // regardless of the admin shell's header height or padding above us.
    const top   = el.getBoundingClientRect().top;
    const headH = el.querySelector('thead')?.getBoundingClientRect().height ?? 56;
    const space = window.innerHeight - top - headH - PAGINATION_H - BOTTOM_GUTTER;
    return Math.max(MIN_ROWS, Math.floor(space / rowHeight));
  }, []);

  useLayoutEffect(() => {
    const recalc = () => {
      if (activeTab === 0 && wardAutoFit) {
        const n = measureRows(wardTableRef.current, WARD_ROW_H);
        if (n) setWardRowsPerPage((prev) => (prev === n ? prev : n));
      }
      if (activeTab === 1 && bedAutoFit) {
        const n = measureRows(bedTableRef.current, BED_ROW_H);
        if (n) setBedRowsPerPage((prev) => (prev === n ? prev : n));
      }
    };

    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
    // wards.length / beds.length are in here because the stat cards above the
    // table can wrap to a second line as the data grows, which moves the table
    // down and changes how much room is left.
  }, [activeTab, wardAutoFit, bedAutoFit, wards.length, beds.length, isMobile, measureRows]);

  // rowsPerPage is no longer constant (it changes on resize and on tab switch),
  // so the current page can fall past the end of the data — which would render
  // an empty table with no obvious cause. Pull it back into range.
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredWards.length / wardRowsPerPage) - 1);
    if (wardPage > maxPage) setWardPage(maxPage);
  }, [filteredWards.length, wardRowsPerPage, wardPage]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredBeds.length / bedRowsPerPage) - 1);
    if (bedPage > maxPage) setBedPage(maxPage);
  }, [filteredBeds.length, bedRowsPerPage, bedPage]);

  // MUI warns when rowsPerPage isn't one of the offered options, and the fitted
  // value is whatever the screen happens to allow — so offer it alongside the
  // fixed choices.
  const wardRowsOptions = useMemo(
    () => [...new Set([wardRowsPerPage, 5, 10, 25, 50])].sort((a, b) => a - b),
    [wardRowsPerPage]
  );
  const bedRowsOptions = useMemo(
    () => [...new Set([bedRowsPerPage, 5, 10, 25, 50])].sort((a, b) => a - b),
    [bedRowsPerPage]
  );

 const handleOpenDialog = async (type, item = null) => {
  setDialogType(type);
  setEditingItem(item);
  
  if (item) {
    setFormData(item);
    
    // If editing a ward, load and pre-fill insurance rates
    if (type === 'ward') {
      // First, load all insurances
      await loadInsurances();
      
      // Then, if the ward has insurance rates, pre-fill them
      if (item.insuranceRates && item.insuranceRates.length > 0) {
        // Map the ward's insurance rates to the insurances
        setInsuranceRates(prevInsurances => {
          return prevInsurances.map(insurance => {
            // Find matching rate for this insurance
            const wardRate = item.insuranceRates.find(
              rate => rate.insuranceId === insurance.insuranceId
            );
            
            if (wardRate) {
              return {
                insuranceId: insurance.insuranceId,
                name: insurance.name,
                dailyTariff: wardRate.dailyTariff || 0,
                dailyRate: wardRate.dailyRate || 0
              };
            }
            
            // If no rate found, return default
            return {
              insuranceId: insurance.insuranceId,
              name: insurance.name,
              dailyTariff: 0,
              dailyRate: 0
            };
          });
        });
      }
    }
  } else {
    setFormData({
      name: '',
      type: '',
      capacity: '',
      dailyRate: '',
      bedNumber: '',
      wardId: '',
      status: 'Available'
    });
  }
  
  setOpenDialog(true);
};

  const handleCloseDialog = () => {
  setOpenDialog(false);
  setEditingItem(null);
  setApplyAll(false);
  setGlobalDailyRate("");
  setGlobalDailyTariff("");
  setInsuranceRates([]);
  setErrors({
    name: false,
    capacity: false,
    dailyRate: false
  });
};

const handleSaveWard = async () => {
  // 1️⃣ Basic ward validation
  const newErrors = {
    name: !formData.name.trim(),
    capacity: !formData.capacity || Number(formData.capacity) <= 0,
  };

  setErrors(newErrors);

  if (Object.values(newErrors).includes(true)) {
    ErrorMessage("Error", "Please correct the errors before saving.");
    return;
  }

  // 2️⃣ Insurance pricing validation
  const invalidInsurance = insuranceRates.some(
    (r) =>
      r.dailyRate === "" ||
      r.dailyTariff === "" ||
      isNaN(Number(r.dailyRate)) ||
      isNaN(Number(r.dailyTariff))
  );

  if (invalidInsurance) {
    ErrorMessage(
      "Error",
      "Daily Rate and Daily Tariff cannot be empty for any insurance."
    );
    return;
  }

  // 3️⃣ Confirm action
  const isEditing = editingItem && editingItem.id;
  const actionText = isEditing ? "Update" : "Create";
  
  const result = await Swal.fire({
    title: `${actionText} Ward?`,
    text: `Do you want to ${actionText.toLowerCase()} ward "${formData.name}" with ${formData.capacity} beds?`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: `Yes, ${actionText}`,
    cancelButtonText: "Cancel",
  });

  if (!result.isConfirmed) return;

  // 4️⃣ Prepare data
  const wardData = {
    name: formData.name,
    capacity: Number(formData.capacity),
    status: "Active",
    insuranceRates: insuranceRates.map((r) => ({
      insuranceId: r.insuranceId,
      insuranceName: r.name,
      dailyRate: Number(r.dailyRate),
      dailyTariff: Number(r.dailyTariff),
    })),
  };

  // 5️⃣ Submit to backend
  try {
    let response;
    
    if (isEditing) {
      // UPDATE existing ward
      response = await axios.post(updateWarddb, {
        wardId: editingItem.id,
        ...wardData
      });
    } else {
      // CREATE new ward
      response = await axios.post(addWardWithBeds, wardData);
    }

    if (response.data.success) {
      successMessage(
        "Success", 
        isEditing 
          ? "Ward and insurance rates updated successfully" 
          : "Ward created with insurance pricing"
      );
      handleCloseDialog();
      loadWards();
      loadBeds();
    } else {
      ErrorMessage("Error", response.data.message);
    }
  } catch (error) {
    console.error(`Error ${isEditing ? 'updating' : 'creating'} ward:`, error);
    ErrorMessage("Error", `Failed to ${isEditing ? 'update' : 'create'} ward`);
  }
};


  const handleSaveBed = () => {
    const ward = wards.find(w => w.id === parseInt(formData.wardId));
    if (editingItem) {
      setBeds(beds.map(b => b.id === editingItem.id ? {
        ...b,
        ...formData,
        wardName: ward?.name,
        dailyRate: ward?.dailyRate
      } : b));
    } else {
      const newBed = {
        id: beds.length + 1,
        ...formData,
        wardName: ward?.name,
        patientName: '',
        admissionDate: '',
        dailyRate: ward?.dailyRate
      };
      setBeds([...beds, newBed]);
    }
    handleCloseDialog();
  };

 const handleDeleteWard = async (id) => {
  const ward = wards.find(w => w.id === id);
  
  if (!ward) {
    ErrorMessage("Error", "Ward not found");
    return;
  }

  // Check if ward has occupied beds
  if (ward.occupied > 0) {
    ErrorMessage(
      "Cannot Delete",
      `This ward has ${ward.occupied} occupied bed(s). Please discharge or transfer patients before deleting.`
    );
    return;
  }

  const result = await Swal.fire({
    title: "Delete Ward?",
    html: `
      <div style="text-align: left;">
        <p><strong>Ward:</strong> ${ward.name}</p>
        <p><strong>Capacity:</strong> ${ward.capacity} beds</p>
        <p><strong>Available Beds:</strong> ${ward.capacity - ward.occupied}</p>
        <p><strong>Insurance Rates:</strong> ${ward.insuranceRates?.length || 0} configured</p>
        <br/>
        <p style="color: #d32f2f; font-weight: 600;">
          ⚠️ This will permanently remove:
        </p>
        <ul style="color: #666;">
          <li>The ward</li>
          <li>All ${ward.capacity} beds</li>
          <li>All insurance pricing configurations</li>
        </ul>
      </div>
    `,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it",
    cancelButtonText: "Cancel"
  });

  if (!result.isConfirmed) return;

  try {
    const response = await axios.post(deleteWard, { wardId: id });

    if (response.data.success) {
      successMessage("Deleted", response.data.message || "Ward removed successfully");
      await loadWards();
      await loadBeds();
    } else {
      ErrorMessage("Error", response.data.message);
    }
  } catch (error) {
    console.error("Delete ward error:", error);
    ErrorMessage("Error", "Failed to delete ward");
  }
};

  
const handleDeleteBed = async (id) => {
  const bed = beds.find(b => b.id === id);
  
  if (!bed) {
    ErrorMessage("Error", "Bed not found");
    return;
  }

  // Check bed status
  if (bed.status === 'Occupied') {
    ErrorMessage(
      "Cannot Delete",
      "This bed is currently occupied. Please discharge the patient first."
    );
    return;
  }

  const result = await Swal.fire({
    title: "Delete Bed?",
    html: `
      <div style="text-align: left;">
        <p><strong>Bed Number:</strong> ${bed.bedNumber}</p>
        <p><strong>Ward:</strong> ${bed.wardName}</p>
        <p><strong>Status:</strong> ${bed.status}</p>
        <br/>
        <p style="color: #d32f2f; font-weight: 600;">
          ⚠️ This will:
        </p>
        <ul style="color: #666;">
          <li>Permanently remove this bed</li>
          <li>Reduce ward capacity by 1</li>
        </ul>
      </div>
    `,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it",
    cancelButtonText: "Cancel"
  });

  if (!result.isConfirmed) return;

  try {
    const res = await axios.post(deleteBed, { bedId: id });

    if (res.data.success) {
      successMessage("Deleted", res.data.message || "Bed removed and ward capacity updated");
      await loadBeds();
      await loadWards(); // ✅ Reload wards to show updated capacity
    } else {
      ErrorMessage("Error", res.data.message);
    }

  } catch (err) {
    console.error("Delete bed error:", err);
    ErrorMessage("Error", err.response?.data?.message || "Server error");
  }
};


  const getStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'success';
      case 'Occupied': return 'error';
      case 'Maintenance': return 'warning';
      case 'Reserved': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        Wards & Beds Management
      </Typography>

      <Alert severity="info" sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
        Manage hospital wards, bed allocation, and pricing. Changes sync with Admissions, Billing, and Ward Dashboard.
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Wards" icon={<LocalHospital />} iconPosition="start" sx={{ minWidth: { xs: 100, sm: 120 } }} />
          <Tab label="Beds" icon={<Bed />} iconPosition="start" sx={{ minWidth: { xs: 100, sm: 120 } }} />
        </Tabs>
      </Box>

      {/* Wards Tab */}
      {activeTab === 0 && (
        <Box>
          <Box sx={{ 
            mb: 2, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1.5, sm: 2 }
          }}>
            <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Ward Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog('ward')}
              fullWidth={isMobile}
              size={isMobile ? 'medium' : 'large'}
            >
              Add Ward
            </Button>
          </Box>

          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3}}} >
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
                    <LocalHospital color="primary" sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                        {wards.length}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>
                        Total Wards
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
                    <Bed color="success" sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                        {wards.reduce((acc, w) => acc + w.capacity, 0)}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>
                        Capacity
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
                    <CheckCircle color="error" sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                        {wards.reduce((acc, w) => acc + w.occupied, 0)}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>
                        Occupied
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
                    <MonetizationOn color="warning" sx={{ fontSize: { xs: 28, sm: 32, md: 40 } }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>
                        {((wards.reduce((acc, w) => acc + w.occupied, 0) / wards.reduce((acc, w) => acc + w.capacity, 0)) * 100 || 0).toFixed(1)}%
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>
                        Rate
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
            <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider' }}>
              <TextField
                fullWidth
                placeholder="Search wards..."
                value={wardSearchQuery}
                onChange={(e) => {
                  setWardSearchQuery(e.target.value);
                  setWardPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    </InputAdornment>
                  ),
                }}
                size="big"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              />
            </Box>

            <TableContainer ref={wardTableRef} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: { xs: 500, sm: 650, md: 750 } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, minWidth: { xs: 120, sm: 150 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Ward
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: 80, display: { xs: 'none', sm: 'table-cell' }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <People sx={{ fontSize: 18, color: '#1976d2' }} />
                        Cap.
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: 80, display: { xs: 'none', sm: 'table-cell' }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <PersonOutline sx={{ fontSize: 18, color: '#d32f2f' }} />
                        Occ.
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: 80, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <CheckCircle sx={{ fontSize: 18, color: '#2e7d32' }} />
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Avail.</Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: 80, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Status
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: { xs: 80, sm: 100 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedWards.length > 0 ? (
                    paginatedWards.map((ward) => (
                      <TableRow key={ward.id} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                            <Hotel sx={{ color: '#1976d2', fontSize: { xs: 18, sm: 20 } }} />
                            <Box>
                              <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {ward.name}
                              </Typography>
                              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {ward.capacity} beds • {ward.occupied} occ.
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: { xs: 28, sm: 32 },
                              height: { xs: 28, sm: 32 },
                              borderRadius: '50%',
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 600,
                              fontSize: { xs: 12, sm: 14 }
                            }}
                          >
                            {ward.capacity}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: { xs: 28, sm: 32 },
                              height: { xs: 28, sm: 32 },
                              borderRadius: '50%',
                              bgcolor: '#ffebee',
                              color: '#d32f2f',
                              fontWeight: 600,
                              fontSize: { xs: 12, sm: 14 }
                            }}
                          >
                            {ward.occupied}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: { xs: 28, sm: 32 },
                              height: { xs: 28, sm: 32 },
                              borderRadius: '50%',
                              bgcolor: '#e8f5e9',
                              color: '#2e7d32',
                              fontWeight: 600,
                              fontSize: { xs: 12, sm: 14 }
                            }}
                          >
                            {ward.capacity - ward.occupied}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={ward.status} 
                            color="success" 
                            size="small"
                            sx={{ 
                              fontSize: { xs: '0.6875rem', sm: '0.8125rem' },
                              height: { xs: 20, sm: 24 }
                            }} 
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenDialog('ward', ward)}
                              sx={{ p: { xs: 0.5, sm: 1 } }}
                            >
                              <Edit sx={{ fontSize: { xs: 16, sm: 18 } }} />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteWard(ward.id)} 
                              color="error"
                              sx={{ p: { xs: 0.5, sm: 1 } }}
                            >
                              <Delete sx={{ fontSize: { xs: 16, sm: 18 } }} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: { xs: 4, sm: 8 } }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          No wards found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={wardRowsOptions}
              component="div"
              count={filteredWards.length}
              rowsPerPage={wardRowsPerPage}
              page={wardPage}
              onPageChange={(e, newPage) => setWardPage(newPage)}
              onRowsPerPageChange={(e) => {
                setWardAutoFit(false);   // explicit choice wins over auto-fit
                setWardRowsPerPage(parseInt(e.target.value, 10));
                setWardPage(0);
              }}
              sx={{
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  mb: { xs: 0, sm: 0 }
                },
                '.MuiTablePagination-select': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                },
                '.MuiTablePagination-actions': {
                  ml: { xs: 1, sm: 2 }
                }
              }}
            />
          </Paper>
        </Box>
      )}

      {/* Beds Tab */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ 
            mb: 3, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2
          }}>
            <Typography variant="h5">Bed Management</Typography>
          </Box>

          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <TextField
                fullWidth
                placeholder="Search beds by number or id"
                value={bedSearchQuery}
                onChange={(e) => {
                  setBedSearchQuery(e.target.value);
                  setBedPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                size="big"
              />
            </Box>

            <TableContainer ref={bedTableRef} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: { xs: 650, md: 750 } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Bed Number</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Ward</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: 100 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 150, display: { xs: 'none', md: 'table-cell' } }}>
                      Patient Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120, display: { xs: 'none', lg: 'table-cell' } }}>
                      Admission Date
                    </TableCell>
                   
                    <TableCell align="center" sx={{ fontWeight: 600, minWidth: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedBeds.length > 0 ? (
                    paginatedBeds.map((bed) => (
                      <TableRow key={bed.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Bed fontSize="small" />
                            <Box>
                              <Typography variant="body2">{bed.bedNumber}</Typography>
                              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                {bed.patientName && (
                                  <Typography variant="caption" color="text.secondary">
                                    {bed.patientName}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{bed.wardName}</Typography>
                          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                            <Typography variant="caption" color="text.secondary">
                              {bed.dailyRate}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={bed.status}
                            color={getStatusColor(bed.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {bed.patientName || '-'}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          {bed.admissionDate || '-'}
                        </TableCell>
                       
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {bed.status === 'Available' && (
                              <IconButton size="small" color="primary" title="Assign Patient">
                                <PersonAdd fontSize="small" />
                              </IconButton>
                            )}
                           
                            <IconButton size="small" onClick={() => handleDeleteBed(bed.id)} color="error">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          No beds found matching your search criteria
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={bedRowsOptions}
              component="div"
              count={filteredBeds.length}
              rowsPerPage={bedRowsPerPage}
              page={bedPage}
              onPageChange={(e, newPage) => setBedPage(newPage)}
              onRowsPerPageChange={(e) => {
                setBedAutoFit(false);   // explicit choice wins over auto-fit
                setBedRowsPerPage(parseInt(e.target.value, 10));
                setBedPage(0);
              }}
              sx={{
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
            />
          </Paper>
        </Box>
      )}

      {/* Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
       <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #19b934ff 0%, #19b934ff 100%)",
            color: "white",
            pb: 1,
            pt: 2,
          }}
        >
          {dialogType === 'ward' 
            ? (editingItem ? `Edit Ward - ${editingItem.name}` : 'Add New Ward')
            : (editingItem ? 'Edit Bed' : 'Add New Bed')
          }
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogType === 'ward' ? (
  <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>

    {/* Ward Name */}
    <Grid item xs={12} size={6}>
      <TextField
        fullWidth
        label="Ward Name"
        value={formData.name}
        error={errors.name}
        helperText={errors.name ? "Ward name is required" : ""}
        onChange={(e) =>
          setFormData({ ...formData, name: e.target.value })
        }
      />
    </Grid>

    {/* Capacity */}
    <Grid item xs={12} sm={6} size={6}>
      <TextField
        fullWidth
        type="number"
        label="Capacity"
        value={formData.capacity}
        error={errors.capacity}
        helperText={errors.capacity ? "Capacity must be positive" : ""}
        onChange={(e) =>
          setFormData({ ...formData, capacity: e.target.value })
        }
      />
    </Grid>
 
    {/* GLOBAL RATE */}
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        type="number"
        label="Daily Rate (Apply to all)"
        value={globalDailyRate}
        onChange={(e) => setGlobalDailyRate(e.target.value)}
      />
    </Grid>

    {/* GLOBAL TARIFF */}
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        type="number"
        label="Daily Tariff (Apply to all)"
        value={globalDailyTariff}
        onChange={(e) => setGlobalDailyTariff(e.target.value)}
      />
    </Grid>

    {/* APPLY TO ALL */}
    <Grid item xs={12}>
      <FormControlLabel
  control={
    <Checkbox
      checked={applyAll}
      onChange={(e) => {
        const checked = e.target.checked;
        setApplyAll(checked);

        if (!checked) {
          // 🔥 Clear global inputs only
          setGlobalDailyRate("");
          setGlobalDailyTariff("");
        }
      }}
    />
  }
  label="Apply Daily Rate and Tariff to all insurance companies"
/>

    </Grid>

    {/* INSURANCE TABLE */}
    <Grid item xs={12}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><b>Insurance</b></TableCell>
              <TableCell><b>Daily Tariff (GH₵)</b></TableCell>
              <TableCell><b>Daily Rate (GH₵)</b></TableCell>
            </TableRow>
          </TableHead>

         <TableBody>
  {insuranceRates.map((row, index) => (
    <TableRow key={row.insuranceId}>
      <TableCell>{row.name}</TableCell>

      <TableCell>
        <TextField
          size="small"
          type="number"
          fullWidth
          value={row.dailyTariff}
          onChange={(e) => {
            const updated = [...insuranceRates];
            updated[index].dailyTariff = e.target.value;
            setInsuranceRates(updated);
          }}
          inputProps={{ min: 0, step: 0.01 }}
        />
      </TableCell>

      <TableCell>
        <TextField
          size="small"
          type="number"
          fullWidth
          value={row.dailyRate}
          onChange={(e) => {
            const updated = [...insuranceRates];
            updated[index].dailyRate = e.target.value;
            setInsuranceRates(updated);
          }}
          inputProps={{ min: 0, step: 0.01 }}
        />
      </TableCell>
    </TableRow>
  ))}
</TableBody>
        </Table>
      </TableContainer>
    </Grid>

  </Grid>
) : (

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bed Number"
                    value={formData.bedNumber}
                    onChange={(e) => setFormData({ ...formData, bedNumber: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Ward</InputLabel>
                    <Select
                      value={formData.wardId}
                      label="Ward"
                      onChange={(e) => setFormData({ ...formData, wardId: e.target.value })}
                    >
                      {wards.map((ward) => (
                        <MenuItem key={ward.id} value={ward.id}>
                          {ward.name} - {ward.type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      {bedStatuses.map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={dialogType === 'ward' ? handleSaveWard : handleSaveBed}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}